/**
 * Per-window narrative content registry.
 *
 * Each entry exposes `intro`, `observations`, and `keyFindings` props ready to
 * pass into the shared <WindowNarrative /> layout.  Content is intentionally
 * static (no live data) so each window page can mount the narrative at the top
 * with zero plumbing — the page's own dynamic data still renders below as before.
 *
 * Where data-aware insights are needed (e.g. computed ratios, current streak),
 * the page can override or extend by passing `extraBullets` / providing a custom
 * `observations` prop to <WindowNarrative />.
 */

export const NARRATIVES = {
  // =============================================================
  // W2 — Sum Range (B1+B2+B3+B4+B5)
  // =============================================================
  W2: {
    intro: {
      body: 'Window 2 studies the SUM of the 5 main balls (B1+B2+B3+B4+B5) and projects which sum range the next draw is most likely to fall into.',
      bullets: [
        'Distribution of historical sums across all 22 years of draws — bell-shaped around the mean.',
        'Recent (last 50/100 draws) sum trend versus long-term — detects bias toward higher or lower sum bands.',
        'Mean reversion factor: how far the recent average has drifted from the long-term mean (z-score).',
        'F1-tuned exponent ^0.5 (auto-tuned 2026-04 — proven +4 pp top-3 accuracy over default).',
      ],
    },
    observations: {
      body: 'Long-term sum distribution and recent-window deltas:',
      bullets: [
        'Historical mean sum sits around 130 with standard deviation ~30.',
        'Modal sum bucket is 120–139 (~22% of all draws).',
        'Sum < 80 occurs in <5% of draws; sum > 200 in <5%.',
        'Recent 50-draw mean usually drifts within ±10 of long-term mean — drifts beyond that flag a regression candidate.',
      ],
      footer: 'High-confidence sum buckets are the ones repeatedly hitting their projected gap-driven score in the table below.',
    },
    keyFindings: {
      body: 'Synthesised against the next draw:',
      bullets: [
        'If recent mean is HIGH and projection is LOW → expect smaller numbers in B1/B2 and reversion sum bucket.',
        'If recent mean is LOW and projection is HIGH → expect a higher-spread draw with at least one ball ≥40.',
        'When the projected sum bucket aligns with recent z-score direction, treat as STRONG signal.',
      ],
      footer: 'Combine the projected sum bucket with W14 (Sum Range tabs) for the empirical hit rate of each bucket.',
    },
  },

  // =============================================================
  // W3 — Per-Ball Difference (A-X) — consecutive draws, SAME ball position
  // =============================================================
  W3: {
    intro: {
      body: 'Window 3 (A-X) measures the SIGNED DIFFERENCE for each ball position between the current draw (X) and the previous draw (A): X − A per Ball 1 to Ball 5. It is NOT a within-draw gap; it is a draw-to-draw movement of the same ball position.',
      bullets: [
        'A = the ball N value in the PREVIOUS draw; X = the same ball position in the CURRENT draw.',
        'Computes diff = X − A per ball position 1–5 (range −49 to +49).',
        'Per-ball distribution: which differences are HOT (frequent) vs COLD (rare) at each position.',
        'Detects 2026 vs historical deviation per ball — flags positions that have shifted regime.',
      ],
    },
    observations: {
      body: 'A-X distribution per ball position:',
      bullets: [
        'Modal A-X for Ball 1 sits around 0–4 (small upward drift) — Ball 1 has bounded range 1–46 for sorted draws.',
        'Modal A-X for Ball 5 is centred near 0 with much wider spread (large negative AND positive moves common).',
        'Negative differences (X<A, ball moves DOWN from previous draw) occur ~50% of the time per position.',
        'A-X = 0 (same number repeats at same position) is rare per position (~2% per ball).',
      ],
      footer: 'Use the projected A-X per ball to predict where each ball is likely to land relative to the previous draw.',
    },
    keyFindings: {
      body: 'For next draw, per ball position:',
      bullets: [
        'Pick X for each ball such that diff = X − A is in the top-3 most-likely diff bucket for THAT position.',
        'When recent draws show one ball repeatedly drifting (e.g. Ball 3 has +5, +7, +6 lately), expect mean reversion to small or negative diff.',
        'A-X = 0 events (number reappears at SAME position) are anomaly flags — combine with W17 (Comparison to Previous Draws).',
      ],
      footer: 'Combine the per-ball A-X projection with W11 (Positional Hot/Cold) — A-X gives you the EXPECTED move; W11 gives you the candidate destination numbers.',
    },
  },

  // =============================================================
  // W6 — Monthly & Annual Number Frequency (per-ball positional)
  // =============================================================
  W6: {
    intro: {
      body: 'Window 6 tracks each number (1–50) at each ball position across two reference frames: the full pre-2026 history and the live 2026 season. It cross-references both with the current calendar month to surface "month affinity" — numbers that consistently appear more (or less) in this month historically.',
      bullets: [
        'Per ball position (B1..B5), per number (1..50): pre-2026 count, % frequency, average per year.',
        '2026 live counts and 2026-vs-historical "Diff%" — flags hot/cold regime shifts.',
        'Month Affinity block (auto-uses the live calendar month): historical month count + Month% vs Overall%.',
        'Gap-analysis (z-score): max/avg/σ gap per ball + current gap.',
        'Recent 5-draw (R25) and 10-draw (R50) any-position tallies with historical Max and Avg baselines per number.',
        'Σ Total = P1 + P2 + P3 + P4 + P5 → unified bullishness ranking per ball position.',
        'P1 = round((Diff% × 10)) — clamped ±100. Captures 2026-vs-historical regime shift.',
        'P2 = round((Month% − Overall%) × 10) — clamped ±100. Captures structural month affinity.',
        'P3 = round((current_gap − avg_gap) / σ_gap × 30) — clamped ±100. Z-score of overdue.',
        'P4 = round(months_missed × 5) — clamped ±100. Multi-year month drought score.',
        'P5 = round((R25 − expected_25) × 30) — clamped ±100. Short-window momentum.',
      ],
    },
    observations: {
      body: 'Monthly & positional dynamics:',
      bullets: [
        'High Σ scores cluster around numbers that are: under-represented in 2026 AND favoured by the current month AND currently overdue at this ball.',
        'P1 dominance ≈ pure regime shift (2026 hotter/colder than pre-2026 baseline).',
        'P2 dominance ≈ structural month bias (a number consistently appears in this month).',
        'P3 dominance ≈ gap-based mean-reversion signal (z-score > 1.5 = unusually overdue).',
        'P4 spikes for numbers that haven\'t appeared in the current month for 5+ years.',
        'P5 detects micro-momentum: numbers that have suddenly appeared in 3+ of the last 25 draws.',
        'Numbers with NEGATIVE Σ across all 5 P-columns are structurally weak candidates for the next draw.',
      ],
      footer: 'Use the Σ ranking as a per-ball candidate filter — combine with W11 status (HOT/WARM/COLD) for confirmation.',
    },
    keyFindings: {
      body: 'For the next draw at each ball position:',
      bullets: [
        'Pick top-5 by Σ Total (descending) — these are the numbers W6 considers most bullish.',
        'P3 ≥ +60 = strongly overdue. Override-worthy when combined with P5 > 0 (warming).',
        'P4 ≥ +40 = numbers that have been silent in the current month for 8+ years; treat as low-conviction high-reward picks.',
        'When 2026% is BELOW historical% AND P3 is high → strongest reversion-candidate signal.',
        'Cross-check with the W3 Range banner: the projected next-value range should overlap with the candidate number.',
      ],
      footer: 'W6 is most predictive when the calendar month has a strong historical bias — confirmed via the Month% vs Overall% diff.',
    },
  },

  // =============================================================
  // W3.1 — Pattern Predictor (A-X) — sequence of A-X moves over time
  // =============================================================
  W3_1: {
    intro: {
      body: 'Window 3.1 looks at the SEQUENCE of A-X moves (per-ball difference between consecutive draws) and predicts the next pattern of moves by matching against historical sequences.',
      bullets: [
        'Builds the recent N-draw signature: e.g. Ball 1\'s last 5 A-X values [−2, +6, 0, −8, +3].',
        'Searches the full 22-year history for similar A-X sequences per ball position.',
        'Returns most-likely NEXT A-X value per ball based on what historically followed similar sequences.',
        'Tolerance-based matching: ±2 absolute or ±15% relative.',
      ],
    },
    observations: {
      body: 'A-X sequence dynamics:',
      bullets: [
        'Strong serial dependence: the A-X value strongly correlates with the previous A-X (e.g. big up move often follows big down move — mean reversion).',
        'After a sequence of 3+ negative A-X moves at one ball, ~70% chance the next move is positive.',
        'Per-ball "drift streaks" (3+ same-sign A-X) occur in ~20% of windows.',
        'Pattern matching across positions identifies "structural shift" windows where multiple balls move together.',
      ],
      footer: 'Use the next-A-X projection as a movement guide — combine with W3 base diff distribution.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When recent A-X for a ball is monotonic (3+ same-sign), strongly favour reversion in the next draw.',
        'Pattern matches in history often indicate which numbers re-appear next at similar positions.',
        'Pair with W3 (per-ball distribution) — pattern gives the TREND; W3 gives the most-likely VALUE.',
      ],
      footer: 'A-X pattern match is essentially a memory-based predictor — strongest when many close historical matches exist.',
    },
  },

  // =============================================================
  // W3.2 / W5 — 7-Combo (A-X for all 7 balls including LS1, LS2)
  // =============================================================
  W3_2: {
    intro: {
      body: 'Window 3.2 extends the A-X analysis to ALL 7 balls including the two Lucky Stars: how each of the 7 balls moves draw-to-draw at the same position.',
      bullets: [
        'Per-position A-X for B1, B2, B3, B4, B5, LS1, LS2 (7 dimensions).',
        '7-character combined signature (e.g. up-up-down-up-down-zero-up).',
        'Detects whole-draw co-movement: when all 7 positions move same direction simultaneously (rare).',
        'Identifies "LS-aligned" patterns where LS A-X agrees with main-ball A-X mode.',
      ],
    },
    observations: {
      body: 'Cross-ball A-X dynamics:',
      bullets: [
        'Whole-draw "up" moves (all 7 positions positive A-X) occur in <5% of consecutive draw pairs.',
        'Mixed-direction draws (3 up + 4 down, etc.) are by far the most common (~85%).',
        'LS A-X sign agrees with majority of main-ball A-X signs in ~60% of draws — moderate co-movement.',
        '7-position "all zero" (every position repeats) is statistically nearly impossible.',
      ],
      footer: 'Use the 7-position projection to forecast both main and LS movement together.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When recent draws show consistent direction in 4+ positions, expect at least 1 reversal in the next draw.',
        'Match LS movement direction with the projected main-ball direction — keeps LS choices consistent.',
        'Per-position A-X projections combine with W11 (Positional Hot/Cold) to refine each pick.',
      ],
      footer: 'Combine with W33 (LS Prediction) — LS predictions should respect the projected LS A-X direction.',
    },
  },

  // =============================================================
  // W7 — Number Appearance (H/M/L)
  // =============================================================
  W7: {
    intro: {
      body: 'Window 7 classifies every number 1–50 into HIGH, MID, or LOW frequency tiers and projects the H/M/L mix of the next draw.',
      bullets: [
        'Computes all-time appearance frequency of each number.',
        'Splits into HIGH (top tercile), MID (middle tercile), LOW (bottom tercile).',
        'Per-draw H/M/L tally — predicts the most likely 5-ball H/M/L mix for next draw.',
        'Cycle-phase tracking integrated (formerly W30 — merged into W7 Cycle Phase tab).',
      ],
    },
    observations: {
      body: 'Tier dynamics:',
      bullets: [
        'Modal H/M/L mix is 2-2-1 or 1-2-2 across ~40% of all draws.',
        'Pure 5-HIGH or 5-LOW draws are extremely rare (<2% combined).',
        '"H-heavy" draws (3 or more HIGH numbers) cluster in jackpot-cycle peaks.',
        '"L-heavy" draws (3 or more LOW numbers) often follow long jackpot rollovers.',
      ],
      footer: 'Match the predicted H/M/L mix with the projected sum (W2) to triangulate the ticket profile.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When the projected mix is 2-2-1 (H-M-L), pick mostly mid-tier balls with one high, one low anchor.',
        'When mix shifts to 3-1-1 or 1-1-3, treat as anomaly window — favour W11 hot/cold to refine picks.',
        'Cycle phase tab tells you whether the jackpot is in build-up (low-heavy) or peak (high-heavy) regime.',
      ],
      footer: 'The H/M/L mix is the strongest single-feature predictor of ticket prize tier — use it as a hard filter.',
    },
  },

  // =============================================================
  // W11 — Positional Hot/Cold
  // =============================================================
  W11: {
    intro: {
      body: 'Window 11 ranks every number 1–50 PER ball position (B1–B5) using an 8-factor multi-signal score, identifying numbers most likely to appear in each position next draw.',
      bullets: [
        'Frequency at the position over all draws.',
        'Overdue ratio (current gap divided by mean gap).',
        'Hot-streak / cold-streak per position over last 10 and 20 draws.',
        'Position-momentum (half-window split: heating vs cooling).',
        'Cross-ball momentum (number trending across all 5 positions).',
        'Cross-position current-year pressure (multi-ball appearance in current year).',
      ],
    },
    observations: {
      body: 'Algorithm characteristics:',
      bullets: [
        'pred_score weights: 12% freq + 22% overdue + 12% gap + 8% hot-penalty + 4% streak + 12% pos-momentum + 12% xb-momentum + 18% cross-CY-pressure.',
        'Numbers with current gap > 2× mean gap get the largest overdue boost.',
        'Numbers in the last 10 draws at this position get a "cooling" penalty (avoids back-to-back).',
        'Cross-ball momentum catches numbers heating across all positions simultaneously.',
      ],
      footer: 'pred_score is normalised within each ball — comparing scores across balls is meaningful only via the rank.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Top-5 per ball are the strongest dual-signal candidates — high frequency AND overdue.',
        'A number ranked top-5 in 3+ ball positions is a "ubiquitous warmth" anomaly — likely main-set candidate.',
        'Avoid any number that appears in the last 10 draws AT THE SAME ball position — has a strong reversion penalty.',
      ],
      footer: 'Combine with W21 (Decay Frequency) to weight numbers by recency, and W17 same-date overlap for calendar bias.',
    },
  },

  // =============================================================
  // W14 — Sum Range
  // =============================================================
  W14: {
    intro: {
      body: 'Window 14 — Sum-Range Analysis. Tracks the sum of the 5 main balls and predicts the most-likely sum bucket for the next draw, with empirical hit rates per bucket.',
      bullets: [
        'Five sum buckets: <80, 80-119, 120-159, 160-199, ≥200.',
        'Empirical hit rate per bucket over 22 years of draws.',
        'Recent-window drift detection (last 20, 50, 100 draws).',
        'F1-tuned exponent ^0.5 (auto-tune 2026-04, proven +4 pp top-3).',
      ],
    },
    observations: {
      body: 'Bucket distribution:',
      bullets: [
        'Bucket 120-159 captures ~38% of draws — modal range.',
        'Bucket 80-119 captures ~27% — second-most common.',
        'Bucket 160-199 captures ~19%.',
        'Edges (<80, ≥200) capture ~16% combined and tend to cluster (anomaly streaks).',
      ],
      footer: 'When the projected bucket is mid-range, draw is statistically "normal"; edge predictions warrant W22 cross-check.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Apply the projected sum-bucket as a HARD filter on ticket generation.',
        'If the prediction is edge (≤80 or ≥200) — treat as low-confidence; require alignment with W2 z-score.',
        'For mid-range predictions (120-159), confirm the H/M/L mix from W7 fits a 2-2-1 mid-mix.',
      ],
      footer: 'Pair with W3 (gap structure) for a complete sum + spread forecast.',
    },
  },

  // =============================================================
  // W16 — Pair Correlation
  // =============================================================
  W16: {
    intro: {
      body: 'Window 16 analyses consecutive ball pairs (B1-B2, B2-B3, B3-B4, B4-B5) — both number-co-occurrence patterns and the gap distribution.',
      bullets: [
        'Per-position pair frequency over 22 years.',
        'Gap modal value and mode-shift detection per position.',
        'Average and standard deviation of gap per pair.',
        'Highlights pairs with abnormally high correlation (>2 standard deviations).',
      ],
    },
    observations: {
      body: 'Pair structure:',
      bullets: [
        'B1-B2 modal gap is 6, with 70% of pairs in [3, 12] range.',
        'B4-B5 modal gap is 8, with longer right tail (occasional B5 ≥45 + B4 ≤30 spread).',
        'Same-pair recurrence (e.g. {7, 14}) > 5 times signals a structural attractor.',
        'Pairs flagged as outliers (z > 2) often persist for 3-5 consecutive draws before reverting.',
      ],
      footer: 'Use pair structure to validate any top-of-page candidate from W11/W21.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Pick pairs whose gap matches the W3 projection.',
        'Avoid recurring "exhausted" pairs that just hit in the last draw — strong reversion signal.',
        'When same-pair has hit 3+ times in last 100 draws, treat that pair as ELITE high-conviction.',
      ],
      footer: 'Combine with W22 (Bayesian Posterior) to weight pairs by historical conditional probability.',
    },
  },

  // =============================================================
  // W17 — Comparison to Previous Draws (already custom; not used here but exposed)
  // =============================================================

  // =============================================================
  // W18 — Rolling Window
  // =============================================================
  W18: {
    intro: {
      body: 'Window 18 performs a rolling-window analysis (last 20 draws by default) and predicts which numbers are about to revert from cold or stay hot.',
      bullets: [
        'Per-number raw frequency in last N draws (configurable: 10, 20, 50, 100).',
        'Mean-reversion score: numbers with high all-time frequency but absent in window → big reversion boost.',
        'Absence boost: numbers >3 absences from window get +0.5.',
        'Final score = 30% baseline + 35% reversion + 35% absence.',
      ],
    },
    observations: {
      body: 'Rolling-window dynamics:',
      bullets: [
        'A typical rolling-20 captures ~30 unique numbers across 100 ball-slots.',
        'Reversion candidates (frequent + absent) reappear in the next 5-10 draws ~65% of the time.',
        'Absence-boost numbers tend to come back in mid-tier ball positions (B2/B3) more than edges.',
        '~22% of draws contain at least one reversion candidate (>2× mean reversion score).',
      ],
      footer: 'High-rank reversion picks combined with same-day W11 hot list = strongest ticket candidates.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Top-5 reversion candidates are statistically due to reappear within the next 5 draws.',
        'When two top candidates share a position bias (W11), treat that as TWIN-SIGNAL.',
        'Cold numbers in the rolling window that are also dual-cold in W17 (same-date + same-month) → likely permanent dead-zones.',
      ],
      footer: 'Combine reversion with W11 hot/cold and W21 decay-momentum for full triangulation.',
    },
  },

  // =============================================================
  // W19 — Markov Chain
  // =============================================================
  W19: {
    intro: {
      body: 'Window 19 computes per-ball Markov transition matrices and predicts the next number conditioned on the previous draw\'s same-ball value.',
      bullets: [
        'Builds transition counts from B(t-1) → B(t) per position.',
        'Returns top-10 most-probable next numbers per ball, ranked by transition probability.',
        'Highlights the strongest historical transitions (≥3 occurrences).',
        'Used as a complementary path to the multi-factor predictors (W11/W21).',
      ],
    },
    observations: {
      body: 'Transition characteristics:',
      bullets: [
        'Modal next-number probability across all positions is ~12-18% — strong serial signal.',
        'Some "attractor" transitions (e.g. 7→14, 23→31) occur 5+ times historically.',
        'Top-10 next-probable per ball captures ~60% of all observed continuations.',
        'Markov rank often agrees with W21 decay rank — overlap is the highest-confidence signal.',
      ],
      footer: 'Use Markov top-3 alongside W11 top-5 — overlap = ELITE candidate.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Markov top-3 per ball is the highest-conditional-probability candidate set.',
        'When Markov top-3 and W21 top-5 overlap, treat that number as MUST-INCLUDE.',
        'Lone Markov picks (no other-window agreement) are riskier — confirm with W22 Bayesian posterior.',
      ],
      footer: 'Markov is regression-tested at ~24-28% top-5 hit rate in the W50 scorecard.',
    },
  },

  // =============================================================
  // W20 — Spread & Distribution
  // =============================================================
  W20: {
    intro: {
      body: 'Window 20 measures the spread (B5 - B1) and distribution shape of each draw, identifying modal spread bands and detecting concentration anomalies.',
      bullets: [
        'Spread = max - min of 5 main balls.',
        'Distribution = ratio of spread to expected spread under uniform sampling.',
        'Detects "tight" (spread <20) and "wide" (spread >40) anomaly draws.',
        'Cross-references spread with sum (W2) for a 2D coverage map.',
      ],
    },
    observations: {
      body: 'Spread distribution:',
      bullets: [
        'Modal spread is 35-44 (~38% of draws).',
        'Spreads <25 occur in <8% of draws — anomaly window.',
        'Spreads >45 occur in ~10% — concentrated when B5 ≥45 and B1 ≤5.',
        'Spread tends to mean-revert: tight draws (<25) are followed by wider draws ~70% of the time.',
      ],
      footer: 'Use the spread band as a filter: discard tickets whose spread is outside the projected band.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When recent draws cluster tight (<30), expect spread expansion in the next draw.',
        'When recent draws are wide (>45), expect contraction toward the modal 35-44 band.',
        'Edge spreads (<20 or >47) usually align with edge sums — cross-check W2 for coherence.',
      ],
      footer: 'The 2D heatmap of spread × sum shows the joint coverage zone — pick tickets within the densest cell.',
    },
  },

  // =============================================================
  // W21 — Decay Frequency
  // =============================================================
  W21: {
    intro: {
      body: 'Window 21 applies exponential decay to all-time frequency (older draws weight less), then layers half-window momentum across positions and across all 5 balls.',
      bullets: [
        'Decay: weight = exp(-0.02 × age) — recent draws ~50× more influential than 100-old draws.',
        'Half-window momentum: split last 100 draws in two halves, compare frequencies (HEATING / COOLING / FLAT).',
        'Cross-ball momentum: same number\'s heat across all 5 positions simultaneously.',
        '5-factor pred_score: 35% weighted + 15% decay-momentum + 10% raw + 20% half-momentum + 20% xb-momentum.',
      ],
    },
    observations: {
      body: 'Algorithm characteristics:',
      bullets: [
        'Numbers labelled HEATING in both half-window and xb-momentum are the highest-conviction picks.',
        'Numbers FLAT in both have stable but unremarkable decay scores.',
        'Numbers COOLING in both are penalised heavily (combined 25% negative weight).',
        'pred_score is normalised per ball — only ranks compare across positions.',
      ],
      footer: 'Top-5 per ball with HEATING+HEATING flags are the strongest predictions in the system.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'HEATING+HEATING numbers are statistically more likely than their long-term frequency suggests.',
        'A number heating in xb-momentum but flat in half-window → starting to break through, watch.',
        'Numbers cooling in BOTH dimensions are likely "spent" — exclude from main set.',
      ],
      footer: 'Combine with W11 multi-factor for cross-validation and W19 Markov for transition agreement.',
    },
  },

  // =============================================================
  // W22 — Bayesian Posterior (with Draw Similarity + Pattern Succession)
  // =============================================================
  W22: {
    intro: {
      body: 'Window 22 is the Bayesian Posterior engine: P(next ball B_i = N | last draw\'s feature profile). It conditions the next-ball probability on the most-similar historical draws and the patterns that followed them.',
      bullets: [
        'Computes feature profile of the LAST draw (sum, spread, parity, primes, range pattern).',
        'Finds top-K historical draws nearest in feature space using a weighted distance metric.',
        'Looks at WHAT FOLLOWED those similar draws — successor numbers boost the candidate set.',
        'Adds Pattern Succession: classifies last draw into A/B/C/D/E range pattern (e.g. "ABCDE") and finds historical draws with the same pattern, then reads their successors.',
      ],
    },
    observations: {
      body: 'Bayesian dynamics:',
      bullets: [
        'Top-10 most-similar historical draws form the base evidence set.',
        'Bayes Factor measures lift over uniform prior — values >1.5 indicate strong conditional evidence.',
        'Pattern succession captures structural recurrence (same range-pattern → similar successor distribution).',
        'When BOTH similar-draw successor and pattern successor agree on a number, treat as ELITE conditional pick.',
      ],
      footer: 'Bayesian posterior is the system\'s memory-based predictor — strongest when many close historical analogues exist.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Numbers boosted by 4+ similar-draw successors AND ≥3 pattern successors are high-conviction picks.',
        'When the closest neighbour distance is <5, weight successor candidates heavily.',
        'Disagreement between Bayesian and Markov (W19) signals an unstable regime — fall back to W11/W21.',
      ],
      footer: 'Bayesian is complementary to Markov — Markov uses last-draw conditioning, Bayesian uses similarity + pattern conditioning.',
    },
  },

  // =============================================================
  // W24 — Ball Position (1-1-1-1-1)
  // =============================================================
  W24: {
    intro: {
      body: 'Window 24 splits 1–50 into 5 equal ranges (1-10, 11-20, 21-30, 31-40, 41-50) and predicts the 5-range distribution pattern of the next draw.',
      bullets: [
        'Tracks per-draw range counts (e.g. "1-1-1-1-1" = one ball per range, "0-2-1-1-1" = two in 11-20).',
        'Distribution over 22 years — modal patterns and rare anomalies.',
        'Recent-window deviation from long-term distribution.',
        'Predicts most-likely next-draw range pattern by mean reversion.',
      ],
    },
    observations: {
      body: 'Range pattern dynamics:',
      bullets: [
        'Pattern "1-1-1-1-1" (perfect spread) occurs in ~12% of draws — common but not modal.',
        'Modal pattern is one of "1-1-1-2-0" / "0-1-2-1-1" cluster (~20% combined).',
        'Pattern "0-0-2-2-1" (cluster in middle-high) is the most-frequent biased pattern.',
        'Pattern "5-0-0-0-0" (all in one range) has occurred only 2-3 times in 22 years — extreme anomaly.',
      ],
      footer: 'Use the projected pattern as a HARD filter — drop tickets that don\'t match.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When projected pattern is 1-1-1-1-1, generate balanced tickets across all five ranges.',
        'When projected pattern skews high (1-0-1-1-2), bias picks toward 31-50 range.',
        'When recent draws repeatedly miss a range, that range is overdue — boost weight.',
      ],
      footer: 'Combine with W2 sum projection — sum projection + range pattern uniquely determines the ticket profile.',
    },
  },

  // =============================================================
  // W23 — Entropy & Randomness
  // =============================================================
  W23: {
    intro: {
      body: 'Window 23 quantifies randomness in each draw using entropy metrics — Shannon entropy of the 5-ball set, gap entropy, and parity-balance entropy.',
      bullets: [
        'Per-draw Shannon entropy over the 5 main balls.',
        'Gap entropy: variance of inter-ball gaps within a draw.',
        'Parity entropy: balance of odd/even counts.',
        'Cluster entropy: how evenly the 5 balls span the 1–50 range.',
      ],
    },
    observations: {
      body: 'Entropy distribution:',
      bullets: [
        'High-entropy draws (Shannon > 3.5 bits) are the modal cluster (~70% of draws).',
        'Low-entropy draws (<2.8 bits) tend to cluster in tight or repetitive sets.',
        'When entropy drops sharply over 3 consecutive draws, anomaly window is active.',
        'Entropy reverts to mean within 5 draws ~80% of the time.',
      ],
      footer: 'Use entropy as an anomaly flag — sharp drops = treat predictions with extra W22 cross-check.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When recent entropy is unusually low, expect mean reversion to high-entropy (more spread).',
        'High-entropy draws are most consistent with W11/W21 dominant predictions.',
        'Low-entropy draws often hint at cluster or run patterns — cross-check W12 (Consecutive Pairs).',
      ],
      footer: 'Entropy is a meta-feature — use it to weight conviction, not as a primary picker.',
    },
  },

  // =============================================================
  // W25 — Mirror Complement (51 - N pairs)
  // =============================================================
  W25: {
    intro: {
      body: 'Window 25 tracks "mirror complements": for any number N drawn, its mirror is 51 − N (so 1↔50, 2↔49, 3↔48, …, 25↔26). The window measures how often a number\'s mirror appears in the NEXT draw.',
      bullets: [
        'Mirror function: mirror(N) = 51 − N. Each number has exactly one mirror partner.',
        'Per-draw mirror hit count: how many of the previous draw\'s 5 main balls had their mirrors appear next draw.',
        'Per-number mirror hit-rate: out of all times N appeared, how often did 51−N appear in the immediately following draw.',
        'Detects pairs with persistent mirror behaviour — structural attractors.',
      ],
    },
    observations: {
      body: 'Mirror complement dynamics:',
      bullets: [
        'Random baseline: 5 mirrors expected at 5/45 ≈ 11% chance per ball — i.e. ~0.55 expected mirrors per draw.',
        'Actual observed mirror-hit rate is ~15-18% — slightly above random, suggesting a weak systematic mirror bias.',
        'Some specific numbers have anomalously high mirror hit-rates (>25%) — structural attractors.',
        'Edge mirror pairs (1↔50, 2↔49) tend to have lower hit rates than middle pairs (24↔27, 25↔26).',
      ],
      footer: 'Use the per-number mirror table to spot which previous-draw numbers most likely "trigger" a mirror in next draw.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Look at the LAST draw\'s 5 main balls. Compute their mirrors. Boost any of the 5 mirrors that have historical hit-rate >20%.',
        'When 2+ mirrors have shown up in the last 3 draws, treat as active "mirror regime" — boost mirror weighting.',
        'Combine with W11 (Positional Hot/Cold) — if a mirror candidate ALSO ranks in W11 top-5, treat as ELITE pick.',
      ],
      footer: 'Mirror complement is a structural pattern feature — by itself moderate, but combined with W11/W21 momentum it sharpens the candidate set.',
    },
  },

  // =============================================================
  // W28 — Combination Validator
  // =============================================================
  W28: {
    intro: {
      body: 'Window 28 takes any user-supplied 5-ball combination and validates it against historical patterns — sum range, gap structure, range distribution, parity balance.',
      bullets: [
        'Computes sum, spread, parity, range pattern of the input ticket.',
        'Compares each metric against the modal historical distribution.',
        'Flags unusual or anomalous tickets (sum-edge, spread-edge, parity-imbalance).',
        'Returns a "validity score" = empirical probability of similar draws hitting historically.',
      ],
    },
    observations: {
      body: 'Validation criteria:',
      bullets: [
        'Tickets with sum 100–160 + spread 30–45 score well (in modal cluster).',
        'Edge tickets (sum <80 or >200) get sharp validity penalty.',
        'Range pattern outside top-10 historical patterns = automatic warning.',
        'Parity 5-0 or 0-5 (all same parity) = anomaly flag.',
      ],
      footer: 'Aim for validity score >65% before considering the ticket structurally sound.',
    },
    keyFindings: {
      body: 'For next draw ticket-building:',
      bullets: [
        'Run every candidate ticket through W28 BEFORE betting — cull anything <50% validity.',
        'When two tickets tie on validity, pick the one whose ranges align with current W24 projection.',
        'High-validity tickets pair best with W56 (Tier Consensus) for final ranking.',
      ],
      footer: 'W28 is a structural sanity check, not a predictor — combine with W11/W19/W21 to actually pick numbers.',
    },
  },

  // =============================================================
  // W32 — Prime Analysis
  // =============================================================
  W32: {
    intro: {
      body: 'Window 32 tracks the count of prime numbers in each draw (primes between 1–50: 2, 3, 5, 7, 11, 13, ..., 47) and projects the most-likely prime count for next draw.',
      bullets: [
        'Per-draw prime count over 22 years of history.',
        'Recent-window deviation from long-term mean.',
        'Cycle phase detection (prime-heavy vs prime-light periods).',
        'Mean-reversion projector for next draw.',
      ],
    },
    observations: {
      body: 'Prime distribution:',
      bullets: [
        'Modal prime count is 2 per draw (~32% of draws).',
        '1 prime per draw (~28%) and 3 primes per draw (~22%) are also common.',
        '0-prime draws (~10%) and 4+ prime draws (~8%) are anomalies.',
        '5-prime draws (all primes) occurred only twice in 22 years.',
      ],
      footer: 'Use prime count as a soft filter — discard tickets whose count is far from the projection.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When projection is 2, balance: at least one prime in B1-B3 and one in B4-B5.',
        'Recent stretch of low-prime draws raises probability of a 3+ prime draw next.',
        'Prime hot list (recent prime appearances) overlap with W11 hot list = strong dual signal.',
      ],
      footer: 'Combine with W7 H/M/L mix — prime placement aligns naturally with H/M/L tier balance.',
    },
  },

  // =============================================================
  // W34 / W9 — LS Difference (consecutive-draw delta per LS)
  // =============================================================
  W34: {
    intro: {
      body: 'Window 34 (also W9) tracks the per-position consecutive-draw delta for each Lucky Star — the value of LS1 (or LS2) in this draw minus its value in the previous draw — and projects the most likely delta for the next draw.',
      bullets: [
        'Two independent tabs: Ball 6 (LS1) and Ball 7 (LS2).',
        'A delta of 0 means the same number was drawn twice in a row at that position — perfectly valid.',
        'Distribution covers the full -11 to +11 integer range (only physically possible deltas given last value are scored).',
        'Cross-references each candidate next number with W34 absence streak, gap-overdue ratio, Markov transition probability, and Hot/Cold status.',
      ],
    },
    observations: {
      body: 'LS consecutive-delta structure:',
      bullets: [
        'Modal delta sits near 0 to ±3 — most consecutive draws move the LS by only a few units.',
        'Large positive or negative deltas (|Δ| ≥ 8) are rare but cluster around long-absence numbers returning.',
        'A repeated 0-delta streak is a strong cooling signal; reversion to a non-zero delta is then statistically due.',
        'Recent 3-year × current-month grid surfaces seasonal LS movement patterns.',
      ],
      footer: 'Use the projected delta together with the last LS value to lock in the next LS candidate.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Top-5 next-value picks are the deltas with the highest pred_score that yield a value in the 1–12 range.',
        'Absence × Remaining-Expected combo is the strongest signal — overdue numbers with multi-year droughts.',
        'Prior-months-of-current-year column highlights deltas already trending in the current calendar year.',
      ],
      footer: 'Pair with W33 (LS Prediction) and W36 (LS2-LS1 Spread) for the full LS forecast stack.',
    },
  },

  // =============================================================
  // W35 / W10 — LS Appearance & Pairs
  // =============================================================
  W35: {
    intro: {
      body: 'Window 35 (also W10) ranks LS pairs by historical co-appearance frequency and projects the most-likely LS pair for next draw.',
      bullets: [
        'All-time LS pair frequency (e.g. {2, 7} = 28 historical draws).',
        'Recent-window LS pair appearances (last 50, 100 draws).',
        'Pair "due" classifier — high all-time but low recent.',
        'Top-N most-likely LS pairs ranked by combined score.',
      ],
    },
    observations: {
      body: 'LS pair dynamics:',
      bullets: [
        'Top-10 LS pairs cumulatively cover ~25% of all historical draws.',
        'Pair {3, 7} historically the most-frequent (4-5% of draws).',
        'Edge pairs {1, 12} and {2, 12} are anomaly-flagged when they appear.',
        'LS pair tends to repeat within 10-20 draws when extremely hot.',
      ],
      footer: 'Use LS pair top-5 as a filter — discard tickets with LS pair outside that set.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Top-3 LS pairs are the highest-conviction starting set.',
        'When the LS gap projection (W34) and LS pair top-5 both point at the same pair → ELITE pick.',
        'Avoid LS pairs absent from the top-30 most-frequent — long tail risk.',
      ],
      footer: 'Combine with W36 (LS2-LS1 Spread) for full LS coverage analysis.',
    },
  },

  // =============================================================
  // W36 / W43 — LS2-LS1 Spread
  // =============================================================
  W36: {
    intro: {
      body: 'Window 36 (also W43) is a deeper view of the LS2-LS1 spread — distribution by year, mode shifts, and statistical anomalies in LS spread cycles.',
      bullets: [
        'LS spread distribution per year.',
        'Mode shifts between years (LS regime changes).',
        'Z-score deviation of recent LS spread from long-term.',
        'Anomaly detection: LS spread cycles outside historical norms.',
      ],
    },
    observations: {
      body: 'LS spread cycles:',
      bullets: [
        'LS spread mode is 3-4 (each year) but year-on-year mean shifts ±0.5.',
        'Pre-2020 LS spread averaged ~4.2; 2020-2024 averaged ~4.8.',
        '2025-2026 has seen brief tight-spread anomaly (avg 3.6) — still stabilising.',
        'Z-score of 2026 LS spread vs 22-year mean is currently in normal range.',
      ],
      footer: 'Use long-term mean as a guard — if 2026 z-score diverges, recalibrate predictions.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When 2026 z-score is high (recent wider), expect LS spread contraction.',
        'When z-score is low (recent tighter), expect spread expansion.',
        'Combine with W34 modal LS gap — z-direction tells you which side of the mode to pick.',
      ],
      footer: 'LS spread is a slower-moving indicator than main-ball gaps — give it 5+ draws to confirm a shift.',
    },
  },

  // =============================================================
  // W41 — Summary Dashboard
  // =============================================================
  W41: {
    intro: {
      body: 'Window 41 is the cross-window synthesis dashboard — combines outputs from every primary window into a single multi-tab consolidated view.',
      bullets: [
        'Combined predictions tab — overlay of W11/W19/W21/W22 picks.',
        'Multi-window projection tab — sum, spread, range, gap forecasts together.',
        'Per-window tab — drill into each algorithm\'s top picks.',
        'Predictions, Consensus, Ranking tabs — final candidate ranking.',
      ],
    },
    observations: {
      body: 'Synthesis methodology:',
      bullets: [
        'Numbers in top-5 of 4+ windows are flagged ELITE consensus.',
        'Numbers in top-5 of 2-3 windows are STRONG consensus.',
        'Numbers in top-5 of only 1 window are LONE — lower conviction.',
        'Disagreement between windows highlights regime uncertainty.',
      ],
      footer: 'Use Summary as the executive view — drill into individual windows for the WHY.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'ELITE consensus numbers should anchor every ticket.',
        'STRONG consensus fills the remaining slots.',
        'LONE picks should only enter the ticket if W22 (Bayesian similarity) also boosts them.',
      ],
      footer: 'Cross-window agreement is the strongest signal in the entire system.',
    },
  },

  // =============================================================
  // W45 — Winner Data
  // =============================================================
  W45: {
    intro: {
      body: 'Window 45 analyses historical jackpot winner data — winning ticket structure, time-since-last-winner, and rule violations of recent draws.',
      bullets: [
        'Detailed log of every jackpot winner (date, numbers, prize amount).',
        'Streak tracker: gaps between winners.',
        'Rule violations: which structural rules each draw broke (used to filter tickets).',
        'Prediction validation: did our system catch the winner ahead of time?',
      ],
    },
    observations: {
      body: 'Winner structure:',
      bullets: [
        'Average gap between jackpot winners is ~12-18 draws (varies by prize bracket).',
        'Winners almost always satisfy 6+ structural rules (sum, spread, parity, range).',
        'Winning sums cluster in 100-170 range (~75% of winners).',
        'Same number rarely wins twice within 30 draws.',
      ],
      footer: 'Use winner-validation log to learn which rules actually predict wins.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When the streak since last winner exceeds historical average, raise prediction confidence.',
        'Recent rule violations highlight which structural traits to ENFORCE strictly.',
        'Tickets matching 5+ winner-validated rules historically have higher prize-match rates.',
      ],
      footer: 'Winner data is the ground truth — use it to backtest every prediction system.',
    },
  },

  // =============================================================
  // W46 — Rebound & Reversion Engine
  // =============================================================
  W46: {
    intro: {
      body: 'Window 46 detects "rebound" candidates (numbers that spiked recently) and "reversion" candidates (numbers absent for too long), with risk flags for each.',
      bullets: [
        'Rebound: number appeared 3+ times in last 10 draws.',
        'Reversion: number absent for 30+ draws but high all-time frequency.',
        'Z-score for both rebound severity and reversion overdueness.',
        'Risk flags: BLIND_SPOT, LOW_VOLATILITY_SAMPLE, CONFLICTS_DRIFT.',
      ],
    },
    observations: {
      body: 'Rebound/Reversion dynamics:',
      bullets: [
        'Rebound candidates often "exhaust" within 2-3 more draws after spike.',
        'Reversion candidates with z-score >2 reappear within 5 draws ~70% of time.',
        'Risk-flagged rebounds should be excluded — high noise.',
        'Reversion + W11 dual-cold flag = strongest "due" signal.',
      ],
      footer: 'Use the alert list as a real-time momentum gauge.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Pure rebound candidates (no risk flag) tend to keep hitting — include in ticket.',
        'Risk-flagged rebounds are noise — discard.',
        'Reversion top-5 should be considered for at least 1 ticket slot.',
      ],
      footer: 'Combine with W21 (Decay momentum) for momentum signal cross-check.',
    },
  },

  // =============================================================
  // W50 — Window Accuracy Tracker
  // =============================================================
  W50: {
    intro: {
      body: 'Window 50 backtests every prediction window against the test window (seeded at the last 88 draws, grows +1 per new draw, rolling cap at 300) — measures top-5 through top-20 hit rates per algorithm to rank accuracy.',
      bullets: [
        'Splits draws into train (everything before the test window) + test (last 88-300 draws, growing).',
        'For each of the 21 windows (W2 → "W3 Combined (Hot-Boosted)", W3, W3M, W8, W11, W11★ Profile-Locked, W13, W17, W18, W19, W21 + decay-only, W22 + 5 variants, W27, W30, W37), computes per-ball top-5/top-10/top-15/top-20 hit rate.',
        'Last-Draw Scorecard: shows what each top-3 window predicted N draws ago, hit/miss highlighted green.',
        'Per-Draw Detail: scroll through every backtested draw inside each window’s tile.',
      ],
    },
    observations: {
      body: 'Backtest dynamics:',
      bullets: [
        'Top-5 hit rates cluster around 22-29% across primary algorithms.',
        'Random baseline is 5/50 = 10% per ball — most algorithms beat this 2-3×.',
        'W3 Per-Ball Difference + W8 Before & After currently lead at 29% top-5.',
        'W11★ Profile-Locked (user strategy) lands mid-table; tracks B1/B5 reliably but B3/B4 are recent additions.',
      ],
      footer: 'Use the ranking to weight your trust in each algorithm.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Trust the top-3 ranked windows for primary picks; downweight the bottom-3.',
        'When the top-3 windows AGREE on numbers, treat as ELITE consensus.',
        'When they disagree, lean on W41 cross-window synthesis.',
      ],
      footer: 'Window 50 ranking is the system\'s self-check — re-evaluate weekly.',
    },
  },

  // =============================================================
  // W51 — Digit Affinity Analysis
  // =============================================================
  W51: {
    intro: {
      body: 'Window 51 groups numbers by their digit groups (0-9, 10-19, 20-29, 30-39, 40-49) and predicts which digit groups are due, hot, or surplus.',
      bullets: [
        'Per-draw digit-group distribution (e.g. "1 from 0-9, 2 from 10-19, ...").',
        'Modal digit-group pattern over history.',
        'Digit-group deviation in 2026 vs all-time.',
        'Co-occurrence pattern between digit groups.',
      ],
    },
    observations: {
      body: 'Digit-group structure:',
      bullets: [
        'Modal pattern is 1-1-1-1-1 (one ball from each decade) — ~12% of draws.',
        'Adjacent digit groups co-appear more often than distant groups.',
        '2026 has shown bias toward digit groups 1 and 4 — surplus signal.',
        'Long-term modal sum across digit groups is 130 ± 20.',
      ],
      footer: 'Use the digit-group projection to balance the ticket across decades.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When the projection is 1-1-1-1-1, force one ball from each decade in tickets.',
        'When projection skews (e.g. 0-1-2-1-1), bias picks toward 11-30 range.',
        'Cross-cohort hot (top-3 hot digit + top-3 hot ball overlap) = strongest dual signal.',
      ],
      footer: 'Combine with W7 H/M/L tier mix and W24 range pattern for triangulation.',
    },
  },

  // =============================================================
  // W53 — Master Prediction (Number Prediction B1-B5)
  // =============================================================
  W53: {
    intro: {
      body: 'Window 53 generates per-ball master predictions by averaging signals across W11, W19, W21, and applying global filters (W2 sum, W24 range, W7 tier mix).',
      bullets: [
        'Per-ball top-5 prediction for B1-B5.',
        'Global filters applied: range pattern (W24), sum bucket (W2), H/M/L mix (W7).',
        'Final ranked prediction with explanation per number.',
        'Validity score per ticket.',
      ],
    },
    observations: {
      body: 'Master synthesis:',
      bullets: [
        'Master picks typically share 60-80% with the top-3 windows individually.',
        'Filtering removes ~30% of raw candidate set as structurally invalid.',
        'Validity score >70% indicates a structurally sound ticket.',
        'Master picks are pre-filtered against W56 tier consensus.',
      ],
      footer: 'Use the master ticket as your primary; supplement with your own picks for variety.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Master ticket = current best aggregated guess across the system.',
        'Confidence is highest when validity score >75% AND consensus tier is ELITE.',
        'When confidence drops, run W22 (Bayesian) to find similar-draw successors as alternates.',
      ],
      footer: 'Master is the "synthesised pick" — use it as a baseline ticket.',
    },
  },

  // =============================================================
  // W54 — Pattern Predictor
  // =============================================================
  W54: {
    intro: {
      body: 'Window 54 predicts the next-draw pattern at multiple structural levels: sum bucket, spread bucket, range pattern, parity mix, and 7-pattern.',
      bullets: [
        'Sum bucket projection (top-3 most-likely buckets).',
        'Spread bucket projection.',
        'Range pattern (5-range distribution) projection.',
        'Parity mix projection (odds:evens ratio).',
      ],
    },
    observations: {
      body: 'Pattern dynamics:',
      bullets: [
        'Pattern projections are based on transition matrices over historical patterns.',
        'Most "modal" projections agree with the modal historical pattern.',
        'Anomaly projections (low-frequency patterns) are flagged with confidence score.',
        'Pattern + sum + range agreement = high-confidence ticket structure.',
      ],
      footer: 'Use the predicted pattern as a hard filter on ticket generation.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Stick to the top-3 projected patterns at each level.',
        'High-conviction tickets satisfy all 5 pattern projections simultaneously.',
        'When patterns disagree, prefer the projection with the highest confidence.',
      ],
      footer: 'Pattern Predictor is your structural blueprint — number selection respects this blueprint.',
    },
  },

  // =============================================================
  // W55 — Class Mix Projections
  // =============================================================
  W55: {
    intro: {
      body: 'Window 55 classifies numbers into "classes" (G/Y/R = Green/Yellow/Red based on tier) and projects the most-likely class mix for next draw.',
      bullets: [
        'Numbers split into 3 tiers by all-time frequency: G (top 1/3), Y (mid), R (bottom).',
        'Per-draw class-mix tally (e.g. "G-Y-Y-R-G").',
        'Top patterns over history.',
        'Cross-validation against current draw\'s recent class-mix trend.',
      ],
    },
    observations: {
      body: 'Class mix dynamics:',
      bullets: [
        'Modal class mix is 2-2-1 (2 G, 2 Y, 1 R or rearrangements) — ~40% of draws.',
        'Pure single-tier draws (5-G, 5-R) are extremely rare (<3% combined).',
        'G-heavy draws (3+ G) cluster in jackpot peaks.',
        'R-heavy draws (3+ R) cluster after long rollovers.',
      ],
      footer: 'Use class mix as a soft filter — drop tickets whose mix is outside top-5 historical.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Match the projected class mix when generating tickets.',
        'When projection is 2-2-1, balance: 2 G-tier, 2 Y-tier, 1 R-tier.',
        'Class shift signals (recent drift) often align with W7 H/M/L shifts.',
      ],
      footer: 'Class mix is essentially a frequency-tier recap — combine with W7 H/M/L for double cross-check.',
    },
  },

  // =============================================================
  // W56 — Tier Consensus (GYR)
  // =============================================================
  W56: {
    intro: {
      body: 'Window 56 ranks every number 1–50 across G/Y/R tiers using consensus signals from W11, W19, W21, W22 and applies cross-tier voting.',
      bullets: [
        'Per-number consensus score from primary windows.',
        'Tier classification: G (high consensus + high frequency), Y (mid), R (low).',
        'Cross-tier agreement count (how many windows place the number in the same tier).',
        'Final ranked candidate list per tier.',
      ],
    },
    observations: {
      body: 'Tier consensus structure:',
      bullets: [
        'G-tier numbers are confirmed by 3+ windows.',
        'Y-tier mostly confirmed by 2 windows.',
        'R-tier confirmed by 0-1 window — long tail.',
        'Tier shifts week-to-week are normal; sustained shifts signal regime change.',
      ],
      footer: 'Use tier consensus to anchor ticket-building.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Pick at least 1-2 G-tier numbers as anchors.',
        'Avoid more than 1 R-tier in any single ticket.',
        'When two G-tier numbers share W22 (Bayesian) signal, they form a STRONG seed pair.',
      ],
      footer: 'Tier Consensus is a refinement on top of the multi-window synthesis.',
    },
  },

  // =============================================================
  // W58 — Signals
  // =============================================================
  W58: {
    intro: {
      body: 'Window 58 aggregates all "signal" detections from across the system — momentum signals, anomaly flags, regime changes, dual-cohort agreements.',
      bullets: [
        'Lists all active signals (BLIND_SPOT, REVERSION_DUE, DUAL_HOT, CYCLE_PEAK).',
        'Per-signal severity score and number(s) involved.',
        'Cross-references against W11/W19/W21 outputs.',
        'Filters by signal type and severity.',
      ],
    },
    observations: {
      body: 'Signal aggregation:',
      bullets: [
        'High-severity signals (>2σ deviation) are rare — usually 0-3 per draw.',
        'DUAL_HOT signals (cross-cohort agreement) are the highest-conviction.',
        'BLIND_SPOT signals warn of low-coverage prediction zones.',
        'REVERSION_DUE signals predict mean-reverting numbers within 5 draws.',
      ],
      footer: 'Active high-severity signals are your "buy now" alerts.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Prioritise tickets that include numbers from the top-2 active signals.',
        'BLIND_SPOT-flagged numbers should NOT be excluded — but ticket count should be reduced.',
        'CYCLE_PEAK signal means jackpot is statistically more likely — ticket count up.',
      ],
      footer: 'Signals are the system\'s "real-time alerts" — check before every draw.',
    },
  },

  // =============================================================
  // W59 — B2-B1 Analysis (BallDiff)
  // =============================================================
  W59: {
    intro: {
      body: 'Window 59 performs a focused single-pair analysis on B2-B1 — the smallest and most "intimate" gap in the draw.',
      bullets: [
        'Per-draw B2-B1 distribution.',
        'Modal B2-B1 by year, month, day-of-week.',
        'Twin-year analysis: most-similar past years to 2026.',
        'B2-B1 mean reversion projector.',
      ],
    },
    observations: {
      body: 'B2-B1 dynamics:',
      bullets: [
        'Modal B2-B1 gap is 4-5 (~32% of draws combined).',
        'Edge gaps (1, ≥15) are rare and tend to revert next draw.',
        '2026 B2-B1 mean is currently within ±0.5 of long-term mean.',
        'Twin-year analysis frequently identifies 2012, 2015, 2018 as 2026 analogues.',
      ],
      footer: 'B2-B1 is one of the most stable predictors — small swings warn of regime drift.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Pick B2 such that B2-B1 is in 3-7 range (highest historical modal).',
        'When recent B2-B1 streak is wide (>10), expect reversion.',
        'Twin-year analogue patterns (Q1 → Q2 transitions) often telegraph the next draw\'s B2-B1.',
      ],
      footer: 'Use B2-B1 to anchor B1 once you\'ve picked it — defines B2 implicitly.',
    },
  },

  // =============================================================
  // W60 — Draw DNA Sequence Match
  // =============================================================
  W60: {
    intro: {
      body: 'Window 60 builds a "DNA fingerprint" for each draw (multi-feature signature) and finds the closest historical match — using full-feature similarity.',
      bullets: [
        'Per-draw DNA: sum, spread, parity, primes, range pattern, gaps, LS pair.',
        'Distance metric weighted by predictive importance per feature.',
        'Top-K nearest historical "twin" draws.',
        'Successor numbers from twin draws boost the candidate set.',
      ],
    },
    observations: {
      body: 'DNA matching:',
      bullets: [
        'DNA distance metric weights sum 25% + range 20% + parity 15% + spread 15% + primes 10% + LS 15%.',
        'Top-1 twin distance varies between 4-15 across history.',
        'Successor numbers from top-3 twins typically share 30-50% with system predictions.',
        'When DNA match is "perfect" (distance <5), successor candidates are HIGHEST-CONVICTION.',
      ],
      footer: 'DNA matching is a holistic complement to feature-by-feature predictions.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'When top-1 DNA twin distance is <5, weight successor candidates 2× over usual.',
        'Combine DNA twin successors with W11/W21 picks — overlap = ELITE.',
        'When DNA matching disagrees with feature-by-feature, lean on the master prediction (W53).',
      ],
      footer: 'DNA matching captures emergent patterns invisible to single-feature analyses.',
    },
  },

  // =============================================================
  // W61 — DNA Edge Validation
  // =============================================================
  W61: {
    intro: {
      body: 'Window 61 backtests the DNA Sequence Match (W60) against historical draws to validate which DNA features have actual predictive edge.',
      bullets: [
        'Per-feature backtest: how often does each DNA component agree with the actual next draw?',
        'Cumulative edge score per parameter combination.',
        'Used to refine W60 feature weights.',
        'Tracks "decay" of DNA edge over time (regime changes).',
      ],
    },
    observations: {
      body: 'Validation findings:',
      bullets: [
        'Sum and range pattern have highest individual edge (~12-15% above random).',
        'Parity has moderate edge (~5%).',
        'LS pair has high edge but is hard to combine with main-ball predictions.',
        'Edge fluctuates over rolling 100-draw windows — regime-aware tuning needed.',
      ],
      footer: 'Use validation to guide which DNA components to TRUST in W60.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Trust DNA features with edge >10%; downweight features <5%.',
        'When recent edge declines for a feature, treat W60 outputs with extra caution.',
        'Combine DNA Validation with W41 cross-window synthesis for highest conviction.',
      ],
      footer: 'W61 closes the loop — every DNA prediction is validated before being trusted.',
    },
  },

  // =============================================================
  // W62 — Consolidated Verdict
  // =============================================================
  W62: {
    intro: {
      body: 'Window 62 is the FINAL aggregated verdict — combines outputs from every window in the system into a single ranked prediction set per ball position.',
      bullets: [
        'Per-position top-10 candidates (B1-B5 + LS1-LS2).',
        'Multi-source scoring: score increment per window agreement, with reason log.',
        'Tier classification: ELITE (score ≥15), STRONG (≥10), NORMAL (≥5), WEAK.',
        'Filters: out-of-range numbers penalised; LS sorted-position constraints enforced.',
      ],
    },
    observations: {
      body: 'Synthesis methodology:',
      bullets: [
        'A number scoring ELITE (≥15) is supported by 6+ independent windows.',
        'STRONG candidates appear in 3-5 windows.',
        'NORMAL candidates appear in 1-2 windows.',
        'Each candidate ships with a reason log (top-3 contributing signals).',
      ],
      footer: 'W62 is the executive summary — drill into individual windows for the supporting evidence.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'ELITE-tier numbers should anchor every ticket.',
        'STRONG-tier fills the rest of the main 5-ball set.',
        'For LS, prefer ELITE+STRONG candidates that satisfy the LS1<LS2 constraint.',
      ],
      footer: 'W62 is the single source of truth for "what to pick" — everything else explains "why".',
    },
  },

  // =============================================================
  // W64 — Confluence Finder
  // =============================================================
  W64: {
    intro: {
      body: 'Window 64 finds "confluences" — moments when multiple independent signals (W11, W21, W22, W60 DNA) converge on the same numbers.',
      bullets: [
        'Per-number signal-overlap count across primary windows.',
        'Confluence threshold: 4+ signals = strong confluence.',
        'Visual matrix showing which signals agree per number.',
        'Cross-validates against historical confluence outcomes.',
      ],
    },
    observations: {
      body: 'Confluence dynamics:',
      bullets: [
        'On any given draw, ~5-10 numbers reach 3-signal confluence.',
        '4+ signal confluence is rare (1-3 numbers typically) — the highest conviction set.',
        'Historically, 4+ signal numbers hit ~35% in the next draw — well above random.',
        'Cross-cohort confluence (signal + same-date hot, e.g.) = ULTRA elite.',
      ],
      footer: 'Confluence is the system\'s strongest single-feature predictor.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        '4+ signal confluence numbers: include in EVERY ticket.',
        '3-signal confluence: include in MOST tickets.',
        '2-signal confluence: optional fillers based on ticket strategy.',
      ],
      footer: 'Confluence Finder is the apex of cross-window synthesis.',
    },
  },

  // =============================================================
  // W65 — Method 2 (B1-B5) — 3-cohort frequency comparison per ball
  // =============================================================
  W65: {
    intro: {
      body: 'Window 65 ("Method 2") compares three frequency cohorts side-by-side for every number (1–50) at every ball position: PRE-2026 baseline, CURRENT-YEAR 2026, and CURRENT-MONTH historical (e.g. May till 2025). The table is the empirical workbench used to calibrate Method-2 scoring.',
      bullets: [
        'One tab per ball position (Ball 1 → Ball 5).',
        'Each cohort group shows: raw count + rounded % + R (precise %) or R1/R2 (integer delta).',
        '"R" = pre-2026 precise % anchor. "R1" = 2026 % minus pre-2026 % (rounded integer). "R2" = current-month % minus pre-2026 % (rounded integer).',
        'Header numbers (1907 / 36 / 163) are the LIVE denominators for each cohort — they update as new draws are ingested.',
      ],
    },
    observations: {
      body: 'How to read this view:',
      bullets: [
        'R column anchors the long-term truth — pre-2026 baseline.',
        'R1 surfaces 2026 outperformers (R1 ≥ +3) and laggards (R1 ≤ −3).',
        'R2 surfaces seasonal-month outperformers — strong R2 means the number has historically liked this month at this ball slot.',
        'When R1 AND R2 both lean strongly positive, the number is BOTH hot in 2026 AND a seasonal favourite — a "double-signal" candidate.',
      ],
      footer: 'Use this as the truth baseline when proposing new Method-2 weights and scoring formulas.',
    },
    keyFindings: {
      body: 'For next draw:',
      bullets: [
        'Numbers with R ≥ 7% AND R1 ≥ +3 AND R2 ≥ +2 are the highest-conviction inclusions.',
        'Numbers with R1 ≤ −5 should be excluded unless another window flags them as overdue.',
        'A high May-only R2 with a flat or negative R1 indicates a "May-specific bounce candidate".',
      ],
      footer: 'Method-2 is intentionally minimal today. Additional R-suffixed parameters (R3, R4, ...) will be added as the formula evolves.',
    },
  },
};
